import { useState } from "react";
import { alpha, Box, Button, CircularProgress, Divider, TextField, Typography } from "@mui/material";
import {
  SubmitButtonTextEnum,
  SubmitButtonEnum,
  SubmitButtonType,
  TabKeyEnum,
  IPreviewCultureCategory,
} from "../../utils/types";
import { useCreateCultureCategoryMutation, useEditCultureCategoryMutation } from "../../api/apiSlice";
import React from "react";
import toast from "react-hot-toast";
import { useDetectAnyChange, useDisableButtons } from "../../hooks";

type CreateOrEditCultureCategoryProps = {
  activeTab: TabKeyEnum;
  handleClose: () => void;
  category: IPreviewCultureCategory | null;
  isEditing: boolean;
};

export const CreateOrEditCultureCategory = (props: CreateOrEditCultureCategoryProps) => {
  const { handleClose, category, isEditing, activeTab } = props;
  const [formData, setFormData] = React.useState<{ name: string; description: string }>({
    name: category?.name || "",
    description: category?.description || "",
  });
  const [buttonClicked, setButtonClicked] = React.useState<SubmitButtonType | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [createCategory, createResult] = useCreateCultureCategoryMutation();
  const [editCategory, editResult] = useEditCultureCategoryMutation();
  const { isLoading: isLoadingCreate, isSuccess } = createResult;
  const { isSuccess: isEditSuccess, isLoading: isEditLoading } = editResult;

  const isLoading = isLoadingCreate || isEditLoading;

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
    if (event.target.name === "name") {
      if (event.target.value) {
        setError(null);
      } else {
        setError("Name is required");
      }
    }
  };

  const onSubmit = async (e: any) => {
    e.preventDefault();
    const buttonName = e.nativeEvent.submitter.name as SubmitButtonType;
    setButtonClicked(buttonName);

    if (!formData.name.trim()) {
      setError("Name is required");
      return;
    }

    const published = buttonName === SubmitButtonEnum.SaveAsUnpublished ? 0 : -1;

    if (isEditing && category) {
      await editCategory({
        id: category.id,
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        published,
      })
        .unwrap()
        .then(() => {
          toast.success("Category saved successfully");
        })
        .catch(error => {
          const errorMessage = typeof error.data === "string" ? error.data : "Failed to save category";
          toast.error(errorMessage);
        });
    } else {
      await createCategory({
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        published,
      })
        .unwrap()
        .then(() => {
          toast.success("Category created successfully");
        })
        .catch(error => {
          const errorMessage = typeof error.data === "string" ? error.data : "Failed to create category";
          toast.error(errorMessage);
        });
    }
  };

  React.useEffect(() => {
    if (isSuccess || isEditSuccess) {
      handleClose();
    }
  }, [handleClose, isEditSuccess, isSuccess]);

  const hasChanges = useDetectAnyChange<{ name: string; description: string }>({
    initialObject: {
      name: category?.name || "",
      description: category?.description || "",
    },
    object: formData,
  });

  const hasRequiredFields = !!formData.name.trim();

  const { disableReadyForReviewButton, disableSaveAsUnpublishedButton } = useDisableButtons({
    isLoading,
    hasError: !!error,
    hasRequiredFields,
    isEditing,
    hasChanges: hasChanges(),
    activeTab,
  });

  return (
    <form onSubmit={onSubmit}>
      <Box display="flex" flexDirection="column" justifyContent="space-between" sx={{ height: "100%", width: "100%", maxWidth: "100%", minWidth: 0 }}>
        <Box display="flex" flexDirection={"column"} gap={2}>
          <TextField
            required
            inputProps={{ maxLength: 128 }}
            onChange={handleOnChange}
            onBlur={handleOnChange}
            label="Name"
            name="name"
            fullWidth
            value={formData.name}
            error={!!error}
            helperText={error}
            size="small"
          />
          <TextField
            onChange={handleOnChange}
            label="Description"
            name="description"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            size="small"
          />
        </Box>

        <Box>
          <Divider />
          <Box display="flex" justifyContent="space-between" sx={{ py: 3, bottom: 0, gap: 2 }}>
            <Button
              disabled={disableReadyForReviewButton}
              sx={{ borderRadius: "20px" }}
              variant="contained"
              color="primary"
              type="submit"
              name={SubmitButtonEnum.ReadyForReview}
            >
              {isLoading && buttonClicked === SubmitButtonEnum.ReadyForReview && <CircularProgress size={20} />}
              {SubmitButtonTextEnum.ReadyForReview}
            </Button>
            <Button
              disabled={disableSaveAsUnpublishedButton}
              sx={theme => ({ flex: 1, borderRadius: "20px", backgroundColor: alpha(theme.palette.primary.main, 0.5) })}
              variant="contained"
              color="primary"
              type="submit"
              name={SubmitButtonEnum.SaveAsUnpublished}
            >
              {isLoading && buttonClicked === SubmitButtonEnum.SaveAsUnpublished && <CircularProgress size={20} />}
              {SubmitButtonTextEnum.SaveAsUnpublished}
            </Button>
            <Button disableElevation variant="outlined" sx={{ borderRadius: "20px" }} onClick={handleClose} type="button">
              <Typography sx={{ fontSize: "16px" }} color="textPrimary">
                Cancel
              </Typography>
            </Button>
          </Box>
        </Box>
      </Box>
    </form>
  );
};
